import React, { useState, useEffect, useRef } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { MessageCircle, X, Send, Bot, User, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

// Initialize the Gemini API
const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const genAI = new GoogleGenerativeAI(API_KEY);

// We use the gemini-2.5-flash model as it's the recommended model for chat
const model = genAI.getGenerativeModel({
    model: "gemini-2.5-flash",
    systemInstruction: "You are 'Travel Aura Assistant', a helpful and friendly AI travel and accommodation guide for the Travel Aura platform. Your primary purpose is to help users with travel, destinations, itineraries, flight advice, and accommodations. \n\nCRITICAL CONSTRAINTS: \n1. You MUST ONLY answer questions related to travel, geography, lodging, transportation, and tourism.\n2. If a user asks a question that is NOT related to travel (e.g., coding, math, general knowledge, jokes, writing essays), you MUST politely decline and steer the conversation back to travel. For example: \"I'd love to help with that, but I'm the Travel Aura Assistant! I can only help you plan your next trip, find great accommodations, or suggest itineraries. Where would you like to travel next?\"\n3. Keep your answers concise, structured, and easy to read. Use bullet points when suggesting itineraries or lists of hotels."
});

const AITravelAssistant = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        {
            id: 1,
            role: 'assistant',
            text: "Hi there! I'm your Travel Aura Assistant ✨. Where are you dreaming of going next?"
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);
    const [chatSession, setChatSession] = useState(null);

    // Initialize chat session when component mounts
    useEffect(() => {
        try {
            const session = model.startChat({
                history: [],
            });
            setChatSession(session);
        } catch (e) {
            console.error("Failed to initialize chat session:", e);
        }
    }, []);

    // Auto-scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSendMessage = async (e) => {
        e?.preventDefault();
        if (!input.trim() || isLoading || !chatSession) return;

        const userMessage = input.trim();
        setInput('');

        // Add user message to UI
        const newMessages = [...messages, { id: Date.now(), role: 'user', text: userMessage }];
        setMessages(newMessages);
        setIsLoading(true);

        try {
            // Send message to Gemini
            const result = await chatSession.sendMessage(userMessage);
            const response = await result.response;
            let text = response.text();

            // Add assistant response to UI
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                role: 'assistant',
                text: text
            }]);
        } catch (error) {
            console.error("Error calling Gemini API:", error);
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                role: 'assistant',
                text: `I'm having trouble connecting right now (Error: ${error.message}). Please try again later.`
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className="fixed bottom-6 right-6 z-[9999]">
            {/* Chat Window */}
            {isOpen && (
                <div className="absolute bottom-16 right-0 w-[300px] sm:w-[350px] md:w-[400px] h-[600px] max-h-[calc(100vh-100px)] bg-white rounded-3xl shadow-2xl border border-sky-100/50 flex flex-col overflow-hidden animate-in zoom-in-95 duration-300 origin-bottom-right drop-shadow-2xl">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-sky-500 to-blue-600 p-4 md:p-5 flex items-center justify-between text-white relative overflow-hidden">
                        <div className="absolute -right-4 -top-4 bg-white/10 w-24 h-24 rounded-full blur-xl"></div>
                        <div className="absolute -left-4 -bottom-4 bg-white/10 w-20 h-20 rounded-full blur-lg"></div>

                        <div className="flex items-center gap-3 relative z-10">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                                <Bot size={22} className="text-white drop-shadow-md" />
                            </div>
                            <div>
                                <h3 className="font-black text-lg tracking-tight flex items-center gap-1.5">
                                    Travel Aura AI <Sparkles size={14} className="text-sky-200" />
                                </h3>
                                <div className="flex items-center gap-1.5 opacity-90">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 shadow-[0_0_8px_rgba(110,231,183,1)] animate-pulse"></span>
                                    <p className="text-xs font-bold uppercase tracking-widest text-[#e0f2fe]">Online</p>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={() => setIsOpen(false)}
                            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors z-10"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50 flex flex-col gap-4">
                        {messages.map((message) => (
                            <div
                                key={message.id}
                                className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                            >
                                <div className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center shadow-sm ${message.role === 'user' ? 'bg-sky-500 text-white' : 'bg-gradient-to-br from-blue-500 to-sky-400 text-white'
                                    }`}>
                                    {message.role === 'user' ? <User size={14} /> : <Bot size={16} />}
                                </div>

                                <div className={`max-w-[75%] rounded-2xl p-4 text-sm ${message.role === 'user'
                                    ? 'bg-sky-500 text-white rounded-tr-none shadow-md shadow-sky-500/20'
                                    : 'bg-white text-gray-700 rounded-tl-none shadow-sm border border-slate-100 markdown-body'
                                    }`}>
                                    {message.role === 'user' ? (
                                        message.text
                                    ) : (
                                        <ReactMarkdown
                                            components={{
                                                p: ({ node, ...props }) => <p className="mb-3 last:mb-0 leading-relaxed text-slate-700" {...props} />,
                                                strong: ({ node, ...props }) => <strong className="font-bold text-sky-950" {...props} />,
                                                ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-4 space-y-1.5 text-slate-700 marker:text-sky-400" {...props} />,
                                                ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-4 space-y-1.5 text-slate-700 marker:text-sky-500 font-medium" {...props} />,
                                                li: ({ node, ...props }) => <li className="pl-1" {...props} />,
                                                h1: ({ node, children, ...props }) => <h1 className="font-black text-xl text-sky-950 mt-5 mb-3" {...props}>{children}</h1>,
                                                h2: ({ node, children, ...props }) => <h2 className="font-extrabold text-lg text-sky-950 mt-4 mb-2" {...props}>{children}</h2>,
                                                h3: ({ node, children, ...props }) => <h3 className="font-bold text-base text-sky-900 mt-3 mb-1.5" {...props}>{children}</h3>,
                                                h4: ({ node, children, ...props }) => <h4 className="font-semibold text-sm text-sky-800 mt-2 mb-1" {...props}>{children}</h4>,
                                                a: ({ node, children, ...props }) => <a className="text-sky-600 font-medium underline underline-offset-2 hover:text-sky-800 transition-colors" target="_blank" rel="noopener noreferrer" {...props}>{children}</a>,
                                                blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-sky-300 pl-4 py-1 my-3 text-slate-600 italic bg-sky-50/50 rounded-r-lg" {...props} />,
                                                code: ({ node, inline, ...props }) => inline
                                                    ? <code className="bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded-md text-[13px] font-mono" {...props} />
                                                    : <code className="block bg-slate-800 text-slate-200 p-3 rounded-xl overflow-x-auto text-[13px] font-mono my-3 shadow-inner" {...props} />,
                                                pre: ({ node, ...props }) => <pre className="m-0 p-0 bg-transparent" {...props} />,
                                                table: ({ node, ...props }) => <div className="overflow-x-auto my-4 rounded-xl border border-slate-200 shadow-sm"><table className="w-full text-left border-collapse text-sm" {...props} /></div>,
                                                thead: ({ node, ...props }) => <thead className="bg-slate-100/80 text-slate-800 font-semibold border-b border-slate-200" {...props} />,
                                                tbody: ({ node, ...props }) => <tbody className="divide-y divide-slate-100" {...props} />,
                                                tr: ({ node, ...props }) => <tr className="hover:bg-slate-50/50 transition-colors" {...props} />,
                                                th: ({ node, ...props }) => <th className="px-4 py-3 font-semibold text-slate-800 align-middle" {...props} />,
                                                td: ({ node, ...props }) => <td className="px-4 py-3 align-middle text-slate-600" {...props} />
                                            }}
                                        >
                                            {message.text}
                                        </ReactMarkdown>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Loading typing indicator */}
                        {isLoading && (
                            <div className="flex gap-3">
                                <div className="w-8 h-8 flex-shrink-0 rounded-full bg-gradient-to-br from-blue-500 to-sky-400 text-white flex items-center justify-center shadow-sm">
                                    <Bot size={16} />
                                </div>
                                <div className="bg-white rounded-2xl rounded-tl-none p-4 shadow-sm border border-slate-100 flex items-center gap-1.5 w-16 h-[46px]">
                                    <div className="w-1.5 h-1.5 bg-sky-300 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                                    <div className="w-1.5 h-1.5 bg-sky-300 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                                    <div className="w-1.5 h-1.5 bg-sky-300 rounded-full animate-bounce"></div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-4 bg-white border-t border-slate-100">
                        <form
                            onSubmit={handleSendMessage}
                            className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-2xl p-1 pr-2 focus-within:ring-2 focus-within:ring-sky-500/20 focus-within:border-sky-400 transition-all shadow-inner"
                        >
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder="Ask about your next adventure..."
                                className="flex-1 bg-transparent border-none py-3 px-4 focus:outline-none text-sm font-medium text-slate-700 placeholder:text-slate-400"
                                disabled={isLoading}
                            />
                            <button
                                type="submit"
                                disabled={!input.trim() || isLoading}
                                className="w-10 h-10 rounded-xl bg-sky-500 text-white flex items-center justify-center transition-all hover:bg-sky-600 disabled:opacity-50 disabled:hover:bg-sky-500 shadow-md shadow-sky-500/20 disabled:shadow-none"
                            >
                                <Send size={18} className={input.trim() ? 'translate-x-0.5' : ''} />
                            </button>
                        </form>
                        <div className="text-center mt-3">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300 flex items-center justify-center gap-1">
                                Powered by Google Gemini <Sparkles size={10} className="text-sky-300" />
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating Action Button */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="relative group flex items-center justify-center w-16 h-16 bg-gradient-to-br from-sky-400 to-blue-600 rounded-full text-white shadow-xl shadow-sky-500/40 transition-all hover:scale-110 active:scale-95 z-50 overflow-hidden"
                >
                    {/* Animated rings */}
                    <div className="absolute inset-0 rounded-full border-2 border-white/20 animate-ping group-hover:animate-none"></div>
                    <div className="absolute inset-0 bg-white/20 rounded-full scale-0 group-hover:scale-150 transition-transform duration-500"></div>

                    <MessageCircle size={30} className="relative z-10 transition-transform group-hover:-mt-1 group-hover:rotate-12 duration-300 drop-shadow-lg" />
                    <Sparkles size={14} className="absolute top-4 right-4 text-sky-200 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" />
                </button>
            )}
        </div>
    );
};

export default AITravelAssistant;
